import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom } from 'rxjs';
import { User, AuthResponse, Token } from '@core/models';
import { ErrorFrontend } from '@core/errors';
import { environment } from '@environments/environment';
import { ACTING_BRANCH_STORAGE_KEY } from './acting-branch.service';
import { TokenService } from './token.service';
import { WebSocketService } from './websocket.service';

/**
 * Authentication Service
 * Manages user authentication, session, and logout
 * Based on atlas-frontend authentication service
 */
@Injectable({
  providedIn: 'root',
  deps: [Router, HttpClient, TokenService, WebSocketService],
})
export class AuthService {
  /** User */
  private _user: User | undefined;
  private _userPromise$: Promise<User | undefined> | undefined;

  public get user(): Promise<User | undefined> {
    if (this._userPromise$) return this._userPromise$;
    if (this._user) return Promise.resolve(this._user);

    this._userPromise$ = this._getUser$().then((user: User | undefined) => {
      this._user = user;
      this._userPromise$ = undefined;
      return user;
    });

    return this._userPromise$;
  }

  constructor(
    private readonly _router: Router,
    private readonly _http: HttpClient,
    private readonly _tokenService: TokenService,
    private readonly _webSocketService: WebSocketService,
  ) {
    // Initialize authentication state on service creation
    this._initializeAuth$();
  }

  /**
   * Initialize authentication state
   */
  private async _initializeAuth$(): Promise<void> {
    if (this._tokenService.accessToken) {
      const user = await this.user;
      // Connect WebSocket on app init if already authenticated
      if (user) this._connectPrescriptionsSocket();
    }
  }

  /**
   * Gets the authenticated user from the server
   * @returns {Promise<User | undefined>} - The authenticated user, if exists
   */
  private async _getUser$(): Promise<User | undefined> {
    return await firstValueFrom(this._http.get<User>('auth/me/')).catch(() => undefined);
  }

  /**
   * Logs in the user
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {Promise<AuthResponse>} - Authentication response with tokens and user
   * @throws {ErrorFrontend} - If login fails
   */
  public async login$(username: string, password: string): Promise<AuthResponse> {
    const response = await firstValueFrom(
      this._http
        .post<AuthResponse>('auth/login/', {
          username,
          password,
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );

    // Validate response
    if (!response.access || !response.refresh) {
      throw new ErrorFrontend('Invalid server response: missing authentication tokens');
    }

    if (!response.user) {
      throw new ErrorFrontend('Invalid server response: missing user information');
    }

    // Save token
    const token: Token = {
      access: response.access,
      refresh: response.refresh,
    };
    this._tokenService.saveToken(token);

    // Save user
    this._user = response.user;

    // Connect WebSocket after successful login
    this._connectPrescriptionsSocket();

    return response;
  }

  /**
   * Logs out the user
   */
  public async logout$(): Promise<void> {
    await firstValueFrom(
      this._http
        .post('auth/logout/', {
          token: this._tokenService.refreshToken,
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    ).catch(() => undefined);

    this.clear();
    this._tokenService.clearToken();
    this._webSocketService.disconnect('intake');
    this._router.navigate(['login']);
  }

  /**
   * Clears stored data in the service
   */
  public clear(): void {
    this._user = undefined;
    this._userPromise$ = undefined;
    // The acting-branch choice is session-scoped to the login. Only the key is
    // imported, not ActingBranchService itself, to avoid a DI cycle.
    sessionStorage.removeItem(ACTING_BRANCH_STORAGE_KEY);
  }

  private _connectPrescriptionsSocket(): void {
    const token = this._tokenService.accessToken;
    if (!token) return;
    this._webSocketService.connect('intake', `${environment.wsUrl}/intake/`, {
      token,
    });
  }
}
